import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { resolveReferralCode } from "../lib/api";
import { AvatarChip } from "./AvatarChip";

/**
 * RefLandingPage — `/r/:code` deep link target.
 *
 * Stores the referral code in localStorage so AuthContext can post
 * /referral/claim once the user signs in. Resolves the code to show the
 * referrer's avatar/name as a trust signal, then nudges sign-in or auto-
 * redirects if the user is already authenticated.
 */
export function RefLandingPage({ onSignIn, user, onToast }) {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referrer, setReferrer] = useState(null);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ target: 10, pro_days: 30 });

  useEffect(() => {
    if (!code) return;
    const c = code.toLowerCase();
    try { localStorage.setItem("lhub_ref", c); } catch { /* ignore */ }
    resolveReferralCode(c)
      .then((res) => {
        setReferrer(res.referrer);
        setMeta({ target: res.target, pro_days: res.pro_days });
      })
      .catch(() => setError("This referral link is invalid or expired."));
  }, [code]);

  // If they're already signed in, the AuthContext effect will fire claim
  // automatically — nudge them onward.
  useEffect(() => {
    if (!user || !referrer) return;
    onToast?.(`Welcome! You joined via ${referrer.name}.`);
    const t = setTimeout(() => navigate("/", { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [user, referrer, navigate, onToast]);

  return (
    <main className="page-z min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="ref-landing">
      <div className="glass max-w-md w-full rounded-3xl p-8 text-center">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-link mr-1" />REFERRAL INVITE
        </p>
        {error ? (
          <>
            <h1 className="font-display text-3xl grad-text mt-4">LINK EXPIRED</h1>
            <p className="text-sm text-white/60 mt-3">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              data-testid="ref-back-home"
              className="btn-ghost text-xs tracking-widest px-5 py-2.5 rounded-2xl mt-6"
            >
              ← BACK TO LEAGUE HUB
            </button>
          </>
        ) : referrer ? (
          <>
            <div className="flex justify-center mt-5">
              <AvatarChip avatarId={referrer.avatar_id} pfpUrl={referrer.pfp_url} size={96} />
            </div>
            <h1 className="font-display text-3xl grad-text mt-3">
              <span data-testid="ref-referrer-name">{referrer.name}</span> sent you to LEAGUE HUB
            </h1>
            <p className="text-sm text-white/75 mt-3 leading-relaxed">
              Join the neon arena — sign up and complete your profile.
              You'll be supporting <b className="text-white">{referrer.name}</b>'s
              path to <b className="text-[#14b8a6]">{meta.pro_days} days of
              PRO free</b> once they bring {meta.target} new operators.
            </p>
            {user ? (
              <p className="text-[11px] text-[#14b8a6] mt-5" data-testid="ref-already-signed">
                You're already signed in. Redirecting…
              </p>
            ) : (
              <button
                type="button"
                onClick={onSignIn}
                data-testid="ref-signin-btn"
                className="btn-neon px-8 py-3 rounded-2xl mt-6 font-bold tracking-widest text-sm"
              >
                🚀 JOIN NOW · FREE
              </button>
            )}
            <p className="text-[10px] text-white/40 mt-4">
              Free forever. Upgrade to PRO PLAYER anytime for $1.99/mo.
            </p>
          </>
        ) : (
          <p className="text-sm text-white/50 mt-6">Resolving link…</p>
        )}
      </div>
    </main>
  );
}

export default RefLandingPage;
